import { useCallback, useMemo } from 'react';
import { spaceMimeTypeFromName, SpaceNodeSortBy } from 'shared';

import { VaultHooks } from '../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { Order } from '../../../../types/general.types';
import {
  VaultFile,
  VaultFolder,
  VaultOrder,
  VaultSortBy,
  VaultTab,
} from '../../../../types/vault.types';
import { resolveStorageFileName } from '../../../../utils/file-upload-duplicate-names.util';
import { resolveVaultDisplayName } from '../../../../utils/get-s3file-original-name.util';
import {
  SpaceBrowserFile,
  SpaceBrowserFolder,
  SpaceBrowserSort,
} from '../../types/data-room.types';
import { isVaultDocusignFile } from '../utils/vault-file.util';
import { inVaultNameOrder, withPinnedVaultFoldersFirst } from '../utils/vault-folder-order.util';
import { isVaultSharedFolder } from '../utils/vault-shared-folder.util';

type Props = {
  // Off for every other space, so the Vault's routes are never asked on their behalf, and while a
  // search's results are shown instead of the level.
  enabled: boolean;
  // Null at the top of the Vault, which lists its system folders and holds no files of its own.
  folderId: null | string;
  // A Stakeholder's Vault: no folder takes a file, whatever its own rule says.
  readOnly?: boolean;
  sort: SpaceBrowserSort;
};

const NO_FILES: SpaceBrowserFile[] = [];
const NO_FOLDERS: SpaceBrowserFolder[] = [];
const NO_LINKS = new Map<string, string>();
const NO_NAMES: string[] = [];

const VAULT_SORT_BY: Record<SpaceNodeSortBy, VaultSortBy> = {
  [SpaceNodeSortBy.LAST_EDIT]: VaultSortBy.LAST_EDIT,
  [SpaceNodeSortBy.NAME]: VaultSortBy.NAME,
  [SpaceNodeSortBy.SIZE]: VaultSortBy.SIZE,
};

const toBrowserFolder = ({
  fileCount,
  id,
  name,
  size,
  updatedAt,
}: VaultFolder): SpaceBrowserFolder => ({
  id,
  ...(isVaultSharedFolder(name) ? { isShared: true } : {}),
  itemCount: fileCount,
  name,
  size,
  updatedAt,
});

// A Vault file keeps no type, so it is read from the name the Vault shows. A DocuSign document
// cannot be renamed or deleted, as on the Vault page.
const toBrowserFile = (file: VaultFile, folderId: string): SpaceBrowserFile => {
  const name = resolveVaultDisplayName(file.name, file.link);
  const contentType = spaceMimeTypeFromName(name);
  const isDocusign = isVaultDocusignFile(file);

  return {
    contentType,
    createdAt: file.createdAt,
    editLockReason: isDocusign ? 'DOCUSIGN_VAULT_FILE_ACTION_DISABLED' : undefined,
    folderId,
    id: file.id,
    isDocusignSynced: isDocusign,
    name,
    size: file.size,
    updatedAt: file.updatedAt,
  };
};

/**
 * One level of the Vault's Repository as the Vault page reads it: its system folders at the top,
 * then one folder request per open folder, ordered by the server on any of the Vault's columns.
 * Its `uploadPermission` is the Vault's own rule for where a file may be added, and `fileLinks`
 * keeps the stored link a rename has to send back.
 */
export const useVaultFolderContents = ({ enabled, folderId, readOnly = false, sort }: Props) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const sortBy = VAULT_SORT_BY[sort.by];
  const orderBy = sort.order === Order.DESC ? VaultOrder.DESC : VaultOrder.ASC;
  const isTop = enabled && !folderId;
  const isFolder = enabled && !!folderId;

  // The Vault hooks run whenever they have a company and a folder, so an empty one keeps them off.
  const top = VaultHooks.useRepositoryFolders({
    companyId: isTop ? companyId : '',
    mode: VaultTab.REPOSITORY,
    orderBy,
    search: '',
    sortBy,
  });
  const { folder, isError, isLoading, refetch } = VaultHooks.useVaultFolder({
    companyId,
    folderId: isFolder && folderId ? folderId : '',
    orderBy,
    sortBy,
  });

  // By name the level is ordered here too, so the modules read A to Z whatever the collation; in
  // every column General and Documents lead it.
  const folders = useMemo(() => {
    const level = isTop ? top.folders : isFolder && folder?.folders ? folder.folders : null;

    if (!level) return NO_FOLDERS;

    const browserFolders = level.map(toBrowserFolder);

    return sort.by === SpaceNodeSortBy.NAME
      ? inVaultNameOrder(browserFolders, sort.order)
      : withPinnedVaultFoldersFirst(browserFolders);
  }, [folder?.folders, isFolder, isTop, sort.by, sort.order, top.folders]);

  const files = useMemo(
    () =>
      isFolder && folderId && folder?.files
        ? folder.files.map((file) => toBrowserFile(file, folderId))
        : NO_FILES,
    [folder?.files, folderId, isFolder],
  );

  const fileLinks = useMemo(
    () => (folder?.files ? new Map(folder.files.map(({ id, link }) => [id, link])) : NO_LINKS),
    [folder?.files],
  );

  // The names the folder holds as storage keeps them, so an upload refuses a taken one up front.
  const existingNames = useMemo(
    () => folder?.files?.map((file) => resolveStorageFileName(file.name, file.link)) ?? NO_NAMES,
    [folder?.files],
  );

  const { refetch: refetchTop } = top;
  const retry = useCallback(() => {
    if (isTop) {
      void refetchTop();
    } else {
      void refetch();
    }
  }, [isTop, refetch, refetchTop]);

  return {
    canUpload: !readOnly && isFolder && !!folder?.uploadPermission,
    existingNames,
    fileLinks,
    files,
    folders,
    // A level that failed has nothing to show, which is not the same as an empty one.
    isError: isTop ? top.isError : isFolder && isError && !folder,
    isPending: isTop ? top.isLoading : isFolder && isLoading,
    retry,
  };
};
